import { useEffect, useMemo, useRef, useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import {
  DATE_FIELDS,
  DEFAULT_AGGREGATIONS,
  DEFAULT_SEERIST_REQUEST,
  EXAMPLE_SEERIST_BODY,
  FACETS,
  LOCATION_KEYS,
  QUERY_PARAMS_BY_KIND,
  RELATIVE_UNITS,
  SEERIST_ENDPOINTS,
  getSeeristEndpoint,
  type SeeristRequest,
} from './seeristRequestConstants';

type FacetRow = { key: string; values: string };

type Props = {
  value?: SeeristRequest | null;
  onChange: (request: SeeristRequest) => void;
};

function splitList(value: string): string[] {
  return String(value || '')
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function coerceValue(value: string): string | number | boolean {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value !== '' && !Number.isNaN(Number(value))) return Number(value);
  return value;
}

function rowsToFacetMap(
  rows: FacetRow[]
): Record<string, unknown[]> | undefined {
  const out: Record<string, unknown[]> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) continue;
    const values = splitList(row.values).map(coerceValue);
    if (values.length === 0) continue;
    out[key] = values;
  }
  return Object.keys(out).length ? out : undefined;
}

function facetMapToRows(map: unknown): FacetRow[] {
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map as Record<string, unknown>).map(
    ([key, values]) => ({
      key,
      values: Array.isArray(values)
        ? values.map(String).join(', ')
        : String(values ?? ''),
    })
  );
}

export function SeeristRequestBuilder({ value, onChange }: Props) {
  const initial = value || DEFAULT_SEERIST_REQUEST;
  const [endpointId, setEndpointId] = useState(initial.endpointId);
  const [customPath, setCustomPath] = useState(
    initial.endpointId.startsWith('custom') ? initial.path : ''
  );
  const [contentId, setContentId] = useState(() => {
    const match = initial.path?.match(
      /\/content\/news-social\/([0-9a-fA-F-]{36})$/
    );
    return match?.[1] || '';
  });
  const [queryParams, setQueryParams] = useState<Record<string, string>>(() => {
    const q = initial.query || {};
    return Object.fromEntries(
      Object.entries(q).map(([k, v]) => [k, v == null ? '' : String(v)])
    );
  });
  const [bodyQuery, setBodyQuery] = useState(
    String((initial.body as any)?.query || '')
  );
  const [bodyKeywords, setBodyKeywords] = useState(
    String((initial.body as any)?.keywords || '')
  );
  const [keywordOperator, setKeywordOperator] = useState(
    String((initial.body as any)?.keywordOperator || '')
  );
  const [minScore, setMinScore] = useState(
    (initial.body as any)?.minScore != null
      ? String((initial.body as any).minScore)
      : ''
  );
  const [question, setQuestion] = useState(
    String((initial.body as any)?.question || '')
  );
  const [publishedMode, setPublishedMode] = useState<
    'off' | 'relative' | 'absolute'
  >(() => {
    const p = (initial.body as any)?.publishedAt;
    if (!p) return 'off';
    if (p.from?.value != null || p.to?.value != null) return 'relative';
    return 'absolute';
  });
  const [publishedRelFrom, setPublishedRelFrom] = useState(
    String((initial.body as any)?.publishedAt?.from?.value ?? -7)
  );
  const [publishedRelUnit, setPublishedRelUnit] = useState(
    String((initial.body as any)?.publishedAt?.from?.unit || 'days')
  );
  const [locations, setLocations] = useState<Record<string, string>>(() => {
    const loc = (initial.body as any)?.locations || {};
    const out: Record<string, string> = {};
    for (const key of LOCATION_KEYS) {
      out[key] = Array.isArray(loc[key]) ? loc[key].join(', ') : '';
    }
    return out;
  });
  const [filterRows, setFilterRows] = useState<FacetRow[]>(() =>
    facetMapToRows((initial.body as any)?.filters)
  );
  const [excludeRows, setExcludeRows] = useState<FacetRow[]>(() =>
    facetMapToRows((initial.body as any)?.excludeFilters)
  );
  const [aggregations, setAggregations] = useState<string[]>(() => {
    const agg = (initial.body as any)?.aggregations;
    return Array.isArray(agg) ? agg.map(String) : [...DEFAULT_AGGREGATIONS];
  });
  const [facetMode, setFacetMode] = useState(
    String((initial.body as any)?.facetMode || '')
  );
  const [idsText, setIdsText] = useState(() => {
    const ids = (initial.body as any)?.ids;
    return Array.isArray(ids) ? ids.join('\n') : '';
  });
  const [advancedBody, setAdvancedBody] = useState(!!initial.advancedBody);
  const [rawBody, setRawBody] = useState(() =>
    JSON.stringify(initial.body || EXAMPLE_SEERIST_BODY, null, 2)
  );
  const [rawError, setRawError] = useState<string | null>(null);

  const endpoint = useMemo(() => getSeeristEndpoint(endpointId), [endpointId]);
  const visibleParams = QUERY_PARAMS_BY_KIND[endpoint.kind] || [];

  const resolvedPath = useMemo(() => {
    if (endpoint.kind === 'byId') {
      return contentId
        ? `/content/news-social/${contentId.trim()}`
        : '/content/news-social/{id}';
    }
    if (endpoint.kind === 'custom' || endpoint.kind === 'customGet') {
      const p = customPath.trim();
      return p.startsWith('/') ? p : p ? `/${p}` : '';
    }
    return endpoint.path;
  }, [endpoint, contentId, customPath]);

  const builtRequest = useMemo((): {
    request: SeeristRequest;
    rawError: string | null;
  } => {
    const query: Record<string, string | number> = {};
    for (const key of QUERY_PARAMS_BY_KIND[endpoint.kind] || []) {
      const raw = queryParams[key];
      if (raw === undefined || raw === null || String(raw).trim() === '')
        continue;
      if (key === 'page' || key === 'perPage' || key === 'maxDocuments') {
        const n = Number(raw);
        if (!Number.isNaN(n)) query[key] = n;
      } else if (key === 'summaryFormat') {
        query.format = String(raw);
      } else {
        query[key] = String(raw);
      }
    }

    let body: Record<string, unknown> | undefined;
    let nextRawError: string | null = null;
    if (endpoint.method === 'POST') {
      if (advancedBody) {
        try {
          const parsed = JSON.parse(rawBody || '{}');
          body = parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
          nextRawError = 'Invalid JSON body';
          body = {};
        }
      } else {
        body = {};
        if (endpoint.kind === 'askAnna' && question.trim()) {
          body.question = question.trim();
        }
        if (bodyQuery.trim()) body.query = bodyQuery.trim();
        if (bodyKeywords.trim()) body.keywords = bodyKeywords.trim();
        if (keywordOperator) body.keywordOperator = keywordOperator;
        if (minScore !== '' && !Number.isNaN(Number(minScore))) {
          body.minScore = Number(minScore);
        }
        if (publishedMode === 'relative') {
          const num = Number(publishedRelFrom);
          if (!Number.isNaN(num)) {
            body.publishedAt = {
              from: { value: num, unit: publishedRelUnit || 'days' },
            };
          }
        }
        const locOut: Record<string, string[]> = {};
        for (const key of LOCATION_KEYS) {
          const vals = splitList(locations[key] || '');
          if (vals.length) locOut[key] = vals;
        }
        if (Object.keys(locOut).length) body.locations = locOut;
        const filters = rowsToFacetMap(filterRows);
        if (filters) body.filters = filters;
        const excludeFilters = rowsToFacetMap(excludeRows);
        if (excludeFilters) body.excludeFilters = excludeFilters;
        if (aggregations.length) body.aggregations = aggregations;
        if (facetMode) body.facetMode = facetMode;
        const ids = splitList(idsText.replace(/\n/g, ','));
        if (ids.length) body.ids = ids;
      }
    }

    return {
      request: {
        endpointId,
        method: endpoint.method,
        path: resolvedPath,
        query: Object.keys(query).length ? query : undefined,
        body,
        advancedBody,
      },
      rawError: nextRawError,
    };
  }, [
    endpoint,
    endpointId,
    resolvedPath,
    queryParams,
    advancedBody,
    rawBody,
    question,
    bodyQuery,
    bodyKeywords,
    keywordOperator,
    minScore,
    publishedMode,
    publishedRelFrom,
    publishedRelUnit,
    locations,
    filterRows,
    excludeRows,
    aggregations,
    facetMode,
    idsText,
  ]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    setRawError(builtRequest.rawError);
    onChangeRef.current(builtRequest.request);
  }, [builtRequest]);

  const loadExample = () => {
    setEndpointId('news-social-search');
    setCustomPath('');
    setContentId('');
    setQueryParams({
      page: '1',
      perPage: '20',
      sortBy: 'publishedAt',
      sortOrder: 'desc',
      format: 'list',
    });
    setBodyQuery('');
    setBodyKeywords('');
    setKeywordOperator('');
    setMinScore('');
    setQuestion('');
    setPublishedMode('relative');
    setPublishedRelFrom('-7');
    setPublishedRelUnit('days');
    setLocations(
      Object.fromEntries(LOCATION_KEYS.map((k) => [k, ''])) as Record<
        string,
        string
      >
    );
    setFilterRows([]);
    setExcludeRows([]);
    setAggregations([...DEFAULT_AGGREGATIONS]);
    setFacetMode('counts');
    setIdsText('');
    setAdvancedBody(false);
    setRawBody(JSON.stringify(EXAMPLE_SEERIST_BODY, null, 2));
    setRawError(null);
  };

  const resetBuilder = () => {
    loadExample();
    setPublishedMode('off');
    setAggregations([]);
    setFacetMode('');
    setQueryParams({});
  };

  const toggleAggregation = (facet: string) => {
    setAggregations((prev) =>
      prev.includes(facet) ? prev.filter((f) => f !== facet) : [...prev, facet]
    );
  };

  return (
    <div className="d-grid gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <p className="small text-muted mb-0">
          Configure the Seerist request used when this section generates. Uses
          the org Seerist API key from Organization Settings.
        </p>
        <div className="d-flex gap-2">
          <StylishNewButton
            type="button"
            className="button--secondary button--sm"
            onClick={loadExample}
          >
            Load example
          </StylishNewButton>
          <StylishNewButton
            type="button"
            className="button--secondary button--sm"
            onClick={resetBuilder}
          >
            Reset
          </StylishNewButton>
        </div>
      </div>

      <div>
        <label className="form-label">Endpoint</label>
        <select
          className="form-select"
          value={endpointId}
          onChange={(e) => setEndpointId(e.target.value)}
        >
          {SEERIST_ENDPOINTS.map((ep) => (
            <option key={ep.id} value={ep.id}>
              {ep.label}
            </option>
          ))}
        </select>
        <p className="form-text text-muted mb-0">{endpoint.hint}</p>
      </div>

      {(endpoint.kind === 'custom' || endpoint.kind === 'customGet') && (
        <div>
          <label className="form-label">Custom path</label>
          <StylishNewInput
            value={customPath}
            onChange={(e) => setCustomPath(e.target.value)}
            placeholder="/content/search"
          />
        </div>
      )}

      {endpoint.kind === 'byId' && (
        <div>
          <label className="form-label">Content ID (UUID)</label>
          <StylishNewInput
            value={contentId}
            onChange={(e) => setContentId(e.target.value)}
            placeholder="12345678-90ab-cdef-1234-567890abcdef"
          />
        </div>
      )}

      <div className="small text-muted">
        <strong>{endpoint.method}</strong> {resolvedPath || '(path required)'}
      </div>

      {visibleParams.length > 0 && (
        <details open>
          <summary className="fw-medium mb-2">Query parameters</summary>
          <div className="row g-2">
            {visibleParams.includes('page') && (
              <div className="col-6">
                <label className="form-label">page</label>
                <StylishNewInput
                  type="number"
                  min={1}
                  value={queryParams.page || ''}
                  onChange={(e) =>
                    setQueryParams((p) => ({ ...p, page: e.target.value }))
                  }
                />
              </div>
            )}
            {visibleParams.includes('perPage') && (
              <div className="col-6">
                <label className="form-label">perPage</label>
                <StylishNewInput
                  type="number"
                  min={1}
                  max={100}
                  value={queryParams.perPage || ''}
                  onChange={(e) =>
                    setQueryParams((p) => ({ ...p, perPage: e.target.value }))
                  }
                />
              </div>
            )}
            {visibleParams.includes('sortBy') && (
              <div className="col-6">
                <label className="form-label">sortBy</label>
                <select
                  className="form-select"
                  value={queryParams.sortBy || ''}
                  onChange={(e) =>
                    setQueryParams((p) => ({ ...p, sortBy: e.target.value }))
                  }
                >
                  <option value="">(default)</option>
                  <option value="publishedAt">publishedAt</option>
                  <option value="occurredAt">occurredAt</option>
                  <option value="lastPublishedAt">lastPublishedAt</option>
                  <option value="relevance">relevance</option>
                </select>
              </div>
            )}
            {visibleParams.includes('sortOrder') && (
              <div className="col-6">
                <label className="form-label">sortOrder</label>
                <select
                  className="form-select"
                  value={queryParams.sortOrder || ''}
                  onChange={(e) =>
                    setQueryParams((p) => ({ ...p, sortOrder: e.target.value }))
                  }
                >
                  <option value="">(default)</option>
                  <option value="desc">desc</option>
                  <option value="asc">asc</option>
                </select>
              </div>
            )}
            {visibleParams.includes('format') && (
              <div className="col-6">
                <label className="form-label">format</label>
                <select
                  className="form-select"
                  value={queryParams.format || ''}
                  onChange={(e) =>
                    setQueryParams((p) => ({ ...p, format: e.target.value }))
                  }
                >
                  <option value="">(default)</option>
                  <option value="list">list</option>
                  <option value="full">full</option>
                  <option value="facets">facets</option>
                </select>
              </div>
            )}
            {visibleParams.includes('cursor') && (
              <div className="col-6">
                <label className="form-label">cursor</label>
                <StylishNewInput
                  value={queryParams.cursor || ''}
                  onChange={(e) =>
                    setQueryParams((p) => ({ ...p, cursor: e.target.value }))
                  }
                />
              </div>
            )}
            {visibleParams.includes('summaryFormat') && (
              <div className="col-6">
                <label className="form-label">summary format</label>
                <select
                  className="form-select"
                  value={queryParams.summaryFormat || ''}
                  onChange={(e) =>
                    setQueryParams((p) => ({
                      ...p,
                      summaryFormat: e.target.value,
                    }))
                  }
                >
                  <option value="">(default: 5w1h)</option>
                  <option value="5w1h">5w1h</option>
                  <option value="bluf">bluf</option>
                  <option value="sitrep">sitrep</option>
                </select>
              </div>
            )}
            {visibleParams.includes('maxDocuments') && (
              <div className="col-6">
                <label className="form-label">maxDocuments</label>
                <StylishNewInput
                  type="number"
                  min={10}
                  max={100}
                  value={queryParams.maxDocuments || ''}
                  onChange={(e) =>
                    setQueryParams((p) => ({
                      ...p,
                      maxDocuments: e.target.value,
                    }))
                  }
                />
              </div>
            )}
          </div>
        </details>
      )}

      {endpoint.method === 'POST' && (
        <>
          <div className="form-check">
            <input
              id="seerist-advanced-body"
              className="form-check-input"
              type="checkbox"
              checked={advancedBody}
              onChange={(e) => {
                if (e.target.checked) {
                  setRawBody(
                    JSON.stringify(
                      builtRequest.request.body || EXAMPLE_SEERIST_BODY,
                      null,
                      2
                    )
                  );
                }
                setAdvancedBody(e.target.checked);
              }}
            />
            <label className="form-check-label" htmlFor="seerist-advanced-body">
              Advanced: raw JSON body
            </label>
          </div>

          {advancedBody ? (
            <div>
              <label className="form-label">Raw JSON body</label>
              <textarea
                className="form-control font-monospace"
                rows={12}
                value={rawBody}
                onChange={(e) => setRawBody(e.target.value)}
                spellCheck={false}
              />
              {rawError && (
                <span className="form-text form-error">{rawError}</span>
              )}
            </div>
          ) : (
            <>
              <details open>
                <summary className="fw-medium mb-2">Search body</summary>
                <div className="d-grid gap-2">
                  {endpoint.kind === 'askAnna' && (
                    <div>
                      <label className="form-label">question</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="What are the key security risks in this region?"
                      />
                    </div>
                  )}
                  <div>
                    <label className="form-label">query</label>
                    <StylishNewInput
                      value={bodyQuery}
                      onChange={(e) => setBodyQuery(e.target.value)}
                      placeholder="semantic / natural language"
                    />
                  </div>
                  <div>
                    <label className="form-label">keywords</label>
                    <StylishNewInput
                      value={bodyKeywords}
                      onChange={(e) => setBodyKeywords(e.target.value)}
                      placeholder='+climate -warming "climate change"'
                    />
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label">keywordOperator</label>
                      <select
                        className="form-select"
                        value={keywordOperator}
                        onChange={(e) => setKeywordOperator(e.target.value)}
                      >
                        <option value="">(default: AND)</option>
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label">minScore</label>
                      <StylishNewInput
                        type="number"
                        step={0.05}
                        min={0}
                        value={minScore}
                        onChange={(e) => setMinScore(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </details>

              <details>
                <summary className="fw-medium mb-2">
                  Date ranges (publishedAt)
                </summary>
                <div className="d-grid gap-2">
                  <select
                    className="form-select"
                    value={publishedMode}
                    onChange={(e) =>
                      setPublishedMode(
                        e.target.value as 'off' | 'relative' | 'absolute'
                      )
                    }
                  >
                    <option value="off">off</option>
                    <option value="relative">relative</option>
                  </select>
                  {publishedMode === 'relative' && (
                    <div className="d-flex align-items-end gap-2">
                      <div className="flex-grow-1">
                        <label className="form-label">
                          from (negative = past)
                        </label>
                        <StylishNewInput
                          type="number"
                          value={publishedRelFrom}
                          onChange={(e) => setPublishedRelFrom(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">unit</label>
                        <select
                          className="form-select"
                          value={publishedRelUnit}
                          onChange={(e) => setPublishedRelUnit(e.target.value)}
                        >
                          {RELATIVE_UNITS.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  <p className="form-text text-muted mb-0">
                    Absolute ranges for {DATE_FIELDS.join(', ')} can be set via
                    Advanced JSON.
                  </p>
                </div>
              </details>

              <details>
                <summary className="fw-medium mb-2">Locations</summary>
                <div className="row g-2">
                  {LOCATION_KEYS.map((key) => (
                    <div className="col-6" key={key}>
                      <label className="form-label">{key}</label>
                      <StylishNewInput
                        value={locations[key] || ''}
                        onChange={(e) =>
                          setLocations((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        placeholder="comma-separated"
                      />
                    </div>
                  ))}
                </div>
              </details>

              <details>
                <summary className="fw-medium mb-2">Facet filters</summary>
                <div className="d-grid gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small">filters</span>
                    <StylishNewButton
                      type="button"
                      className="button--secondary button--sm"
                      onClick={() =>
                        setFilterRows((rows) => [
                          ...rows,
                          { key: 'riskTypes', values: '' },
                        ])
                      }
                    >
                      + add
                    </StylishNewButton>
                  </div>
                  {filterRows.map((row, idx) => (
                    <div className="row g-2" key={`f-${idx}`}>
                      <div className="col-5">
                        <select
                          className="form-select"
                          value={row.key}
                          onChange={(e) =>
                            setFilterRows((rows) =>
                              rows.map((r, i) =>
                                i === idx ? { ...r, key: e.target.value } : r
                              )
                            )
                          }
                        >
                          {FACETS.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-5">
                        <StylishNewInput
                          value={row.values}
                          onChange={(e) =>
                            setFilterRows((rows) =>
                              rows.map((r, i) =>
                                i === idx ? { ...r, values: e.target.value } : r
                              )
                            )
                          }
                          placeholder="values"
                        />
                      </div>
                      <div className="col-2">
                        <StylishNewButton
                          type="button"
                          className="button--secondary button--sm"
                          onClick={() =>
                            setFilterRows((rows) =>
                              rows.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          ×
                        </StylishNewButton>
                      </div>
                    </div>
                  ))}
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="small">excludeFilters</span>
                    <StylishNewButton
                      type="button"
                      className="button--secondary button--sm"
                      onClick={() =>
                        setExcludeRows((rows) => [
                          ...rows,
                          { key: 'riskTypes', values: '' },
                        ])
                      }
                    >
                      + add
                    </StylishNewButton>
                  </div>
                  {excludeRows.map((row, idx) => (
                    <div className="row g-2" key={`e-${idx}`}>
                      <div className="col-5">
                        <select
                          className="form-select"
                          value={row.key}
                          onChange={(e) =>
                            setExcludeRows((rows) =>
                              rows.map((r, i) =>
                                i === idx ? { ...r, key: e.target.value } : r
                              )
                            )
                          }
                        >
                          {FACETS.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-5">
                        <StylishNewInput
                          value={row.values}
                          onChange={(e) =>
                            setExcludeRows((rows) =>
                              rows.map((r, i) =>
                                i === idx ? { ...r, values: e.target.value } : r
                              )
                            )
                          }
                        />
                      </div>
                      <div className="col-2">
                        <StylishNewButton
                          type="button"
                          className="button--secondary button--sm"
                          onClick={() =>
                            setExcludeRows((rows) =>
                              rows.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          ×
                        </StylishNewButton>
                      </div>
                    </div>
                  ))}
                </div>
              </details>

              <details>
                <summary className="fw-medium mb-2">Aggregations</summary>
                <div className="d-grid gap-2">
                  <div>
                    <label className="form-label">facetMode</label>
                    <select
                      className="form-select"
                      value={facetMode}
                      onChange={(e) => setFacetMode(e.target.value)}
                    >
                      <option value="">(default)</option>
                      <option value="counts">counts</option>
                      <option value="existence">existence</option>
                    </select>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {FACETS.slice(0, 16).map((facet) => (
                      <label
                        key={facet}
                        className="form-check form-check-inline small mb-0"
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={aggregations.includes(facet)}
                          onChange={() => toggleAggregation(facet)}
                        />
                        <span className="form-check-label">{facet}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </details>

              <details>
                <summary className="fw-medium mb-2">Content IDs</summary>
                <textarea
                  className="form-control"
                  rows={3}
                  value={idsText}
                  onChange={(e) => setIdsText(e.target.value)}
                  placeholder="comma or newline separated, max 100"
                  spellCheck={false}
                />
              </details>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default SeeristRequestBuilder;
